import { LocalDate } from '@spa-frontend/date-lib';
import { computed, observable } from 'mobx';
import React, { RefObject } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { component, initialize } from 'tsdi';
import { wrapRequest } from 'wrap-request';

import { Api } from '../../core/api';
import {
    GetWithdrawableContractsRequestDto,
    GetWithdrawableContractsResponseDto,
    WithdrawContractManualRequestDto,
    WithdrawContractRequestDto
} from '../../core/api/dtos';
import { Field, Form } from '../../core/common/forms';
import { injectTSDI } from '../../core/common/tsdi';
import { required } from '../../core/common/validation';
import { SharedContractDissolutionStore } from '../shared-contract-dissolution-store';

@component
export class ContractWithdrawalStore {
    private api = injectTSDI(Api);

    private sharedStore = injectTSDI(SharedContractDissolutionStore);

    @observable
    private _contractToWithdrawId?: number;

    @observable
    private _hasContracts = false;

    public readonly showContractsRequest = wrapRequest(
        ({
            recaptchaToken,
            memberData
        }: {
            recaptchaToken: string;
            memberData: GetWithdrawableContractsRequestDto;
        }) =>
            this.api.client.ConnectApiContractController.getWithdrawableContracts(
                { recaptchaToken },
                memberData
            )
    );

    public readonly withdrawContractRequest = wrapRequest(
        ({
            recaptchaToken,
            withdrawContractDto
        }: {
            recaptchaToken: string;
            withdrawContractDto: WithdrawContractRequestDto;
        }) =>
            this.api.client.ConnectApiContractController.withdrawContract(
                { recaptchaToken },
                withdrawContractDto
            )
    );

    public readonly withdrawContractManualRequest = wrapRequest(
        ({
            recaptchaToken,
            withdrawContractManualDto
        }: {
            recaptchaToken: string;
            withdrawContractManualDto: WithdrawContractManualRequestDto;
        }) =>
            this.api.client.ConnectApiContractController.withdrawContractManual(
                { recaptchaToken },
                withdrawContractManualDto
            )
    );

    public readonly withdrawalMemberDataForm =
        new Form<GetWithdrawableContractsRequestDto>({
            firstname: new Field<string>('', required()),
            lastname: new Field<string>('', required()),
            dateOfBirth: new Field<LocalDate | undefined>(undefined),
            customerNumber: new Field<string | undefined>(undefined),
            email: new Field<string | undefined>(undefined)
        });

    private withdrawContractForms: Form<WithdrawContractRequestDto>[] = [];

    public readonly withdrawContractManualForm: Form<WithdrawContractManualRequestDto> =
        new Form<WithdrawContractManualRequestDto>({
            firstname: new Field<string>('', required()),
            lastname: new Field<string>('', required()),
            dateOfBirth: new Field<LocalDate | undefined>(undefined),
            customerNumber: new Field<string | undefined>(undefined),
            studioId: new Field<number>(0, required()),
            additionalInformation: new Field<string | undefined>(undefined),
            confirmationEmail: new Field<string | undefined>(undefined)
        });

    @initialize
    protected init = async () => {
        await this.sharedStore.prefillSelectedStudio(
            this.withdrawContractManualForm.wrappedField.studioId,
            'WITHDRAWAL'
        );
    };

    @computed
    public get hasContracts(): boolean {
        return this._hasContracts;
    }

    @computed
    public get contractToWithdrawId(): number | undefined {
        return this._contractToWithdrawId;
    }

    public getWithdrawContractForm = (contractId: number | undefined) =>
        this.withdrawContractForms.find(
            (form) => form.wrappedField.contractId.value === contractId
        );

    public addWithdrawContractForm = (
        contract: GetWithdrawableContractsResponseDto
    ) => {
        if (
            contract.contractId &&
            contract.customerUuid &&
            !this.withdrawContractForms.some(
                (form) =>
                    form.wrappedField.contractId.value === contract.contractId
            )
        ) {
            this.withdrawContractForms.push(
                new Form<WithdrawContractRequestDto>({
                    contractId: new Field<number>(
                        contract.contractId,
                        required()
                    ),
                    customerUuid: new Field<string>(
                        contract.customerUuid,
                        required()
                    ),
                    confirmationEmail: new Field<string | undefined>('')
                })
            );
        }
    };

    public handleMemberDataSubmit = async (
        recaptchaRef: React.RefObject<ReCAPTCHA>
    ) => {
        const memberData = await this.withdrawalMemberDataForm.getValidModel();
        if (!memberData) {
            return;
        }
        if (!memberData.customerNumber?.trim() && !memberData.email?.trim()) {
            return;
        }
        const recaptchaToken = await recaptchaRef.current?.executeAsync();
        if (!recaptchaToken) {
            recaptchaRef.current?.reset();
            return;
        }
        await this.showContractsRequest.request({
            recaptchaToken,
            memberData
        });

        const studioId =
            this.showContractsRequest.result?.length &&
            this.showContractsRequest.result[0].studioId;

        if (
            studioId &&
            this.sharedStore.studios?.some((studio) => studio.id === studioId)
        ) {
            await this.sharedStore.getStudioInfo.request(studioId);
        }

        this._hasContracts = this.showContractsRequest.state === 'fetched';
        recaptchaRef.current?.reset();
    };

    public handleWithdraw = async (
        recaptchaRef: RefObject<ReCAPTCHA>,
        contractId: number
    ) => {
        this._contractToWithdrawId = contractId;
        const memberFieldsValid =
            await this.withdrawalMemberDataForm.validate();
        const withdrawContractDto =
            await this.getWithdrawContractForm(contractId)?.getValidModel();
        if (!memberFieldsValid || !withdrawContractDto) {
            return;
        }
        const recaptchaToken = await recaptchaRef.current?.executeAsync();
        if (!recaptchaToken) {
            recaptchaRef.current?.reset();
            return;
        }
        await this.withdrawContractRequest.request({
            recaptchaToken,
            withdrawContractDto
        });
        recaptchaRef.current?.reset();
    };

    public handleWithdrawManual = async (
        recaptchaRef: RefObject<ReCAPTCHA>
    ) => {
        const memberFieldsValid =
            await this.withdrawalMemberDataForm.validate();
        const { firstname, lastname, dateOfBirth, customerNumber } =
            this.withdrawalMemberDataForm.wrappedField;
        this.withdrawContractManualForm.wrappedField.firstname.reset(
            firstname.value
        );
        this.withdrawContractManualForm.wrappedField.lastname.reset(
            lastname.value
        );
        if (dateOfBirth.value) {
            this.withdrawContractManualForm.wrappedField.dateOfBirth.reset(
                dateOfBirth.value
            );
        }
        this.withdrawContractManualForm.wrappedField.customerNumber.reset(
            customerNumber.value
        );
        const withdrawContractManualDto =
            await this.withdrawContractManualForm?.getValidModel();
        if (!memberFieldsValid || !withdrawContractManualDto) {
            return;
        }
        const recaptchaToken = await recaptchaRef.current?.executeAsync();
        if (!recaptchaToken) {
            return;
        }
        await this.withdrawContractManualRequest.request({
            recaptchaToken,
            withdrawContractManualDto
        });
        recaptchaRef.current?.reset();
    };
}
